import { useNotification } from "hooks/useNotification";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import instance, { getAxiosErrorMessage } from "./axiosInstance";
import userStore, { getUserCompanyId } from "store/user.store";
import { ApiToken } from "../types/apiToken.type";
import { Company, OperatorPrivileges, OperatorPrivilege } from "types/company.type";
import { CompanySettings } from "types/companySettings.type";
import { useCurrentUser } from "api/user.api";
import { Role } from "types/enums";

export const COMPANY_ENDPOINT = "/company";
export enum CompanyQueryKey {
  GetOne = "companygetone",
  GetAll = "companygetall",
}

export const useCompanyList = () =>
  useQuery({
    queryKey: [CompanyQueryKey.GetAll],
    queryFn: async (): Promise<Company[]> => {
      const response = await instance.get(COMPANY_ENDPOINT);
      return response.data;
    },
  });

export const useCompany = (id: number) =>
  useQuery({
    queryKey: [CompanyQueryKey.GetOne, id],
    queryFn: async (): Promise<Company> => {
      const response = await instance.get(`${COMPANY_ENDPOINT}/${id}`);
      return response.data;
    },
    enabled: +(id ?? -1) > 0,
  });

export const useCurrentCompany = () =>
  useCompany(userStore((s) => getUserCompanyId(s)));

export const useHasRightsForFeature = (privilege: OperatorPrivilege) => {
  const { user } = useCurrentUser();
  const { data: company } = useCurrentCompany();
  if (!user || !company) return false;
  if (user.role > Role.Operator) return true;

  return company.operatorPrivileges[privilege];
};

export const useAddCompany = () => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (payload: AddCompanyDTO) => {
      const response = await instance.post(COMPANY_ENDPOINT, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CompanyQueryKey.GetAll],
      });
      notification.info("Företaget skapat");
    },
    onError: (err) => {
      notification.error(getAxiosErrorMessage(err));
    },
  });
};

export const useUpdateCompany = (id: number) => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (payload: UpdateCompanyDTO): Promise<Company> => {
      const response = await instance.put(`${COMPANY_ENDPOINT}/${id}`, payload);
      return response.data;
    },
    onSuccess: () => {
      notification.info(`Ändringarna sparade`);
      queryClient.invalidateQueries({
        queryKey: [CompanyQueryKey.GetOne, id],
      });
    },
    onError: () => {
      notification.error("Något gick fel, kontakta support");
    },
  });
};

export const patchCompany = () => ({
  mutationFn: async ({
    id,
    payload,
  }: {
    id: number;
    payload: PatchCompanyDTO;
  }): Promise<Company> =>
    (await instance.patch(`${COMPANY_ENDPOINT}/${id}`, payload)).data,
});

// Api token
export const createCompanyApiToken = () => ({
  mutationFn: async ({
    companyId,
    name,
  }: {
    companyId: number;
    name: string;
  }): Promise<ApiToken> =>
    (
      await instance.post(`${COMPANY_ENDPOINT}/${companyId}/api-token`, {
        name,
      })
    ).data,
});

export const deleteCompanyApiToken = () => ({
  mutationFn: async ({
    companyId,
    tokenId,
  }: {
    companyId: number;
    tokenId: number;
  }): Promise<{ success: boolean }> =>
    (
      await instance.delete(
        `${COMPANY_ENDPOINT}/${companyId}/api-token/${tokenId}`,
      )
    ).data,
});

// Settings
export const patchCompanySettings = () => ({
  mutationFn: async ({
    id,
    payload,
  }: {
    id: number;
    payload: PatchCompanySettingsDTO;
  }): Promise<CompanySettings> =>
    (await instance.patch(`${COMPANY_ENDPOINT}/${id}/settings`, payload)).data,
});

export type AddCompanyDTO = {
  companyName: string;
  organizationNumber: string;
  personFullName: string;
  email: string;
};

export type UpdateCompanyDTO = {
  name: string;
  organizationNumber: string;
  logoUrl?: string;
  certificateLogoUrl?: string;
  phone?: string;
  address?: string;
  postalCode?: string;
  city?: string;
  contactFullName: string;
  contactPhone: string;
  contactEmail: string;
};

export type PatchCompanyDTO = {
  name?: string;
  organizationNumber?: string;
  logoUrl?: string;
  certificateLogoUrl?: string;
  phone?: string;
  address?: string;
  contactFullName?: string;
  contactEmail?: string;
  contactPhone?: string;
  postalCode?: string;
  city?: string;
  isUppdragSchedulingToAdminActivated?: boolean;
  nvKey?: string;
  nvSecret?: string;
  isNvActive?: boolean;
  operatorPrivileges?: OperatorPrivileges;
};

export type PatchCompanySettingsDTO = {
  nvContactFullName?: string;
  nvContactEmail?: string;
  nvContactPhone?: string;
  isUppdragSchedulingToAdminActivated?: boolean;
  nvKey?: string;
  nvSecret?: string;
  isNvActive?: boolean;
  isNvAutomatic?: boolean;
};
